import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Palette, X } from 'lucide-react';
import useUIStore, { BackgroundStyle } from '@/store/useUIStore';
import { cn } from '@/lib/utils';
import {
  chooseBestTextColor,
  hslToHex,
  generateBackgroundFromNav,
  generateHarmoniousPalette,
  hexToRgb,
  rgbToHex
} from '@/lib/colorWheelUtils';
import { TabNavigation } from './TabNavigation';
import { BackgroundTab } from './BackgroundTab';
import { ThemeTab } from './ThemeTab';
import { ActionButtons } from './ActionButtons';

const ColorWheelIcon: React.FC = () => {
  const {
    backgroundColor,
    setBackgroundColor,
    backgroundSecondaryColor,
    setBackgroundSecondaryColor,
    backgroundStyle,
    setBackgroundStyle,
    backgroundBrightness,
    setBackgroundBrightness,
    backgroundTransparency,
    setBackgroundTransparency,
    containerTransparency,
    setContainerTransparency,
    backgroundSaturation,
    setBackgroundSaturation,
    backgroundScale,
    setBackgroundScale,
    backgroundAnimationSpeed,
    setBackgroundAnimationSpeed,
    // UI Theme customizations
    topNavColor,
    setTopNavColor,
    sidebarColor,
    setSidebarColor,
    uiColor,
    setUIColor,
    textColor,
    setTextColor,
    fontFamily,
    setFontFamily,
    // Container colors
    containerColor900,
    setContainerColor900,
    containerColor800,
    setContainerColor800,
    containerColor700,
    setContainerColor700,
    containerColor600,
    setContainerColor600,
    containerColorsLocked,
    setContainerColorsLocked
  } = useUIStore();
  const iconRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [tempColor, setTempColor] = useState<string>(backgroundColor);
  const [tempSecondaryColor, setTempSecondaryColor] = useState<string>(backgroundSecondaryColor);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'background' | 'theme'>('background');
  const [tempTopNavColor, setTempTopNavColor] = useState<string>(topNavColor);
  const [tempSidebarColor, setTempSidebarColor] = useState<string>(sidebarColor);
  const [tempTextColor, setTempTextColor] = useState<string>(textColor);
  const [activeColorPicker, setActiveColorPicker] = useState<'background' | 'secondary' | 'topNav' | 'sidebar' | 'ui' | 'text'>('background');
  const [navColorsLocked, setNavColorsLocked] = useState(true);
  const [tempUiColor, setTempUiColor] = useState<string>(uiColor);
  const [isDraggingColor, setIsDraggingColor] = useState(false);
  // Container color temp states
  const [tempContainerColor900, setTempContainerColor900] = useState<string>(containerColor900);
  const [tempContainerColor800, setTempContainerColor800] = useState<string>(containerColor800);
  const [tempContainerColor700, setTempContainerColor700] = useState<string>(containerColor700);
  const [tempContainerColor600, setTempContainerColor600] = useState<string>(containerColor600);
  const [activeContainerPicker, setActiveContainerPicker] = useState<'900' | '800' | '700' | '600'>('800');
  const [lastRandomMode, setLastRandomMode] = useState<'normal' | 'hard-contrast' | 'hard-contrast-nav-match' | 'unified-theme'>('normal');
  const [isNavColorsOpen, setIsNavColorsOpen] = useState(true);
  const [isContainerColorsOpen, setIsContainerColorsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const { saveTheme } = useUIStore();

  const handleResetDefaults = () => {
    // Reset theme settings with optimal text contrast
    const defaultNavColor = '#1a1a1a';
    const defaultUiColor = '#242424';

    // Generate background colors - default to light blue for effects
    const defaultBackgroundColors = {
      effectsColor: '#66c7ff', // Light blue effects color (more blue, still lighter than original)
      canvasBgColor: '#12171f' // Brighter with more blue tint
    };

    // Reset background settings
    setBackgroundColor(defaultBackgroundColors.effectsColor); // Effects color
    setBackgroundSecondaryColor(defaultBackgroundColors.canvasBgColor); // Canvas background color
    setBackgroundStyle('liquid');
    setBackgroundBrightness(2.0);
    setBackgroundTransparency(1.0);
    setBackgroundSaturation(1.3);
    setBackgroundScale(1.0);
    setBackgroundAnimationSpeed(1.0);
    setTempColor(defaultBackgroundColors.effectsColor);
    setTempSecondaryColor(defaultBackgroundColors.canvasBgColor);

    const defaultTextColor = chooseBestTextColor(defaultBackgroundColors.canvasBgColor, defaultUiColor, defaultNavColor);

    setTopNavColor(defaultNavColor);
    setSidebarColor(defaultNavColor);
    setUIColor(defaultUiColor);
    setTextColor(defaultTextColor);
    setFontFamily('Inter');
    setTempTopNavColor(defaultNavColor);
    setTempSidebarColor(defaultNavColor);
    setTempUiColor(defaultUiColor);
    setTempTextColor(defaultTextColor);
    setNavColorsLocked(true);
    setContainerTransparency(0.1); // Reset container transparency

    // Reset container colors to match nav colors
    setContainerColor900(defaultNavColor);
    setContainerColor800('#1f1f1f');
    setContainerColor700(defaultUiColor);
    setContainerColor600('#a1a1aa');
    setTempContainerColor900(defaultNavColor);
    setTempContainerColor800('#1f1f1f');
    setTempContainerColor700(defaultUiColor);
    setTempContainerColor600('#a1a1aa');
    setContainerColorsLocked(true);

    // Reset CSS custom properties to defaults
    const resetThemeColors = () => {
      // Reset nav and sidebar colors
      document.documentElement.style.setProperty('--nav-bg-color', '26, 26, 26');
      document.documentElement.style.setProperty('--sidebar-bg-color', '26, 26, 26');
      document.documentElement.style.setProperty('--ui-bg-color', '36, 36, 36');

      // Reset text colors to optimal contrast defaults
      const textRgb = defaultTextColor === '#ffffff'
        ? '255, 255, 255'
        : '0, 0, 0';
      document.documentElement.style.setProperty('--text-color', textRgb);
      document.documentElement.style.setProperty('--text-primary', textRgb);
      document.documentElement.style.setProperty('--text-secondary', '161, 161, 170');

      // Reset container transparency and colors
      document.documentElement.style.setProperty('--container-transparency', '0.15');
      document.documentElement.style.setProperty('--container-bg-color-900', '26, 26, 26');
      document.documentElement.style.setProperty('--container-bg-color-800', '31, 31, 31');
      document.documentElement.style.setProperty('--container-bg-color-700', '36, 36, 36');
      document.documentElement.style.setProperty('--container-bg-color-600', '161, 161, 170');
      document.documentElement.style.setProperty('--text-zinc-100', '244, 244, 245');
      document.documentElement.style.setProperty('--text-zinc-200', '228, 228, 231');
      document.documentElement.style.setProperty('--text-zinc-300', '212, 212, 216');
      document.documentElement.style.setProperty('--text-zinc-400', '161, 161, 170');
      document.documentElement.style.setProperty('--text-zinc-500', '113, 113, 122');

      // Reset font family
      document.documentElement.style.setProperty('--font-family', "'Open Sans', sans-serif");
    };

    resetThemeColors();

    setIsAdvancedOpen(false); // Close advanced options after reset
    setIsNavColorsOpen(true); // Reset nav colors to open
    setIsContainerColorsOpen(false); // Reset container colors to closed
    setActiveTab('background'); // Reset to background tab
  };

  const handleRandomize = () => {
    // Generate harmonious color palette using advanced color theory
    const palette = generateHarmoniousPalette();

    // Decide on randomization mode
    const randomValue = Math.random();
    let randomizationMode: 'normal' | 'hard-contrast' | 'hard-contrast-nav-match' | 'unified-theme';

    if (randomValue < 0.3) { // 30% chance for unified theme (all same color)
      randomizationMode = 'unified-theme';
    } else if (randomValue < 0.6) { // 30% chance for hard contrast
      const isNavBackgroundMatch = Math.random() < 0.6;
      randomizationMode = isNavBackgroundMatch ? 'hard-contrast-nav-match' : 'hard-contrast';
    } else { // 40% chance for normal harmonious mode
      randomizationMode = 'normal';
    }

    setLastRandomMode(randomizationMode);

    const isHardContrast = randomizationMode === 'hard-contrast' || randomizationMode === 'hard-contrast-nav-match';
    const isUnifiedTheme = randomizationMode === 'unified-theme';
    const isHighContrastHarmony = Math.random() >= 0.35 && Math.random() < 0.60; // Complementary scheme

    let navColor: string;
    let uiColor: string;
    let backgroundColors: { effectsColor: string; canvasBgColor: string };

    if (isUnifiedTheme) {
      // UNIFIED THEME MODE: Background, sidebar, and topbar all share the same color
      const unifiedColors = [
        // Classic colors that work well as unified themes
        '#1a1a1a', // Dark grey - sophisticated
        '#000000', // Pure black - dramatic
        '#2a2a2a', // Medium dark grey
        '#ffffff', // Pure white - clean
        '#f8f8f8', // Off-white
        '#e5e5e5', // Light grey
        '#3a3a3a', // Medium grey
        '#4a4a4a', // Light medium grey
        '#6b46c1', // Purple
        '#dc2626', // Red
        '#059669', // Green
        '#0891b2', // Cyan
        '#7c3aed', // Violet
        '#ea580c', // Orange
        '#0d9488', // Teal
        '#2563eb'  // Blue
      ];

      const unifiedBaseColor = unifiedColors[Math.floor(Math.random() * unifiedColors.length)];

      // All elements use the same color
      navColor = unifiedBaseColor;
      backgroundColors = {
        effectsColor: unifiedBaseColor,
        canvasBgColor: unifiedBaseColor
      };

      // Create contrasting UI color
      const rgb = hexToRgb(unifiedBaseColor);
      if (rgb) {
        const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
        if (luminance > 0.5) {
          // Light background - use dark UI
          uiColor = hslToHex(0, 0, 15 + Math.random() * 15);
        } else {
          // Dark background - use light UI
          uiColor = hslToHex(0, 0, 70 + Math.random() * 20);
        }
      } else {
        uiColor = hslToHex(0, 0, 40 + Math.random() * 20);
      }

    } else if (isHardContrast) {
      // HARD CONTRAST MODE: Create dramatic, vibrant combinations
      const brightHues = [
        // Primary bright colors
        { h: 0, name: 'red' }, { h: 30, name: 'orange' }, { h: 60, name: 'yellow' },
        { h: 120, name: 'green' }, { h: 180, name: 'cyan' }, { h: 240, name: 'blue' },
        { h: 270, name: 'magenta' }, { h: 300, name: 'pink' }
      ];

      const vibrantHues = [
        // More vibrant options
        { h: 15, name: 'red-orange' }, { h: 45, name: 'yellow-orange' },
        { h: 75, name: 'yellow-green' }, { h: 135, name: 'green-cyan' },
        { h: 195, name: 'cyan-blue' }, { h: 225, name: 'blue-magenta' },
        { h: 285, name: 'magenta-pink' }, { h: 315, name: 'pink-red' }
      ];

      // Choose effect color - very bright and saturated
      const effectHue = Math.random() < 0.7 ? brightHues[Math.floor(Math.random() * brightHues.length)].h :
        vibrantHues[Math.floor(Math.random() * vibrantHues.length)].h;

      // Create bright, saturated effect color
      const effectColor = hslToHex(effectHue, 90 + Math.random() * 10, 50 + Math.random() * 30);

      // Create nav color first, then decide on background approach
      const navHue = (effectHue + (Math.random() < 0.5 ? 180 : 0)) % 360; // Either same or opposite
      navColor = hslToHex(navHue, 30 + Math.random() * 40, 15 + Math.random() * 15); // Darker nav
      uiColor = hslToHex((navHue + 30) % 360, 40 + Math.random() * 30, 25 + Math.random() * 20);

      // Decide whether to use nav-based background or vibrant contrast background
      let canvasBgColor: string;

      if (Math.random() < 0.6) { // 60% chance to use nav-based background for harmony
        // Use nav color as base, with some variation for depth
        const navRgb = hexToRgb(navColor);
        if (navRgb) {
          const variation = 5 + Math.random() * 15; // Small variation
          canvasBgColor = hslToHex(
            navHue,
            Math.max(10, 30 - variation), // Slightly less saturated
            Math.max(5, 15 - variation * 0.5) // Darker than nav
          );
        } else {
          canvasBgColor = navColor; // Fallback
        }
      } else {
        // Use vibrant contrast backgrounds (but allow some that could be dark/nav-like)
        const backgroundOptions = [
          // Dark backgrounds that could match nav colors
          hslToHex(navHue, 20 + Math.random() * 30, 8 + Math.random() * 7), // Very dark nav-like
          hslToHex(navHue, 15 + Math.random() * 25, 12 + Math.random() * 8), // Dark nav-like
          hslToHex((navHue + 180) % 360, 60 + Math.random() * 40, 15 + Math.random() * 10), // Complementary dark
          // Original vibrant options
          hslToHex((effectHue + 120) % 360, 70 + Math.random() * 30, 15 + Math.random() * 10), // Dark complementary
          hslToHex((effectHue + 180) % 360, 60 + Math.random() * 40, 20 + Math.random() * 15), // Dark analogous
          hslToHex((effectHue + 240) % 360, 80 + Math.random() * 20, 10 + Math.random() * 10), // Very dark triad
          hslToHex((effectHue + 90) % 360, 75 + Math.random() * 25, 30 + Math.random() * 20), // Medium
          hslToHex((effectHue + 270) % 360, 70 + Math.random() * 30, 25 + Math.random() * 15), // Medium
        ];

        canvasBgColor = backgroundOptions[Math.floor(Math.random() * backgroundOptions.length)];
      }

      backgroundColors = {
        effectsColor: effectColor,
        canvasBgColor: canvasBgColor
      };

    } else {
      // NORMAL MODE: Use existing harmonious logic
      navColor = hslToHex(palette.navHue, 60 + Math.random() * 20, 25 + Math.random() * 15);
      uiColor = hslToHex(palette.uiHue, 50 + Math.random() * 30, 50 + Math.random() * 20);

      // Generate background colors based on the final nav color
      backgroundColors = generateBackgroundFromNav(navColor);
    }


    // Intelligent theme selection based on color harmony type
    const themes: BackgroundStyle[] = ['liquid', 'ascii', 'particles', 'topology', 'waveform', 'geometric', 'rain', 'orbit', 'cube3d', 'blank'];

    // Theme weights based on harmony type for better visual synergy
    let themeWeights: number[];
    const rand = Math.random();

    if (rand < 0.35) { // Analogous - prefer flowing themes
      themeWeights = [0.25, 0.08, 0.18, 0.12, 0.12, 0.08, 0.06, 0.06, 0.03, 0.02];
    } else if (rand < 0.60) { // Complementary - prefer dynamic themes
      themeWeights = [0.18, 0.12, 0.20, 0.08, 0.15, 0.10, 0.06, 0.06, 0.03, 0.02];
    } else if (rand < 0.80) { // Triadic - prefer balanced themes
      themeWeights = [0.20, 0.10, 0.15, 0.15, 0.10, 0.12, 0.06, 0.06, 0.04, 0.02];
    } else if (rand < 0.90) { // Split-complementary - prefer organic themes
      themeWeights = [0.22, 0.08, 0.16, 0.14, 0.12, 0.10, 0.08, 0.06, 0.02, 0.02];
    } else { // Tetradic - prefer geometric themes
      themeWeights = [0.15, 0.12, 0.12, 0.10, 0.08, 0.18, 0.08, 0.08, 0.06, 0.03];
    }

    const themeRandomValue = Math.random();
    let themeIndex = 0;
    let cumulativeWeight = 0;
    for (let i = 0; i < themes.length; i++) {
      cumulativeWeight += themeWeights[i];
      if (themeRandomValue <= cumulativeWeight) {
        themeIndex = i;
        break;
      }
    }
    setBackgroundStyle(themes[themeIndex]);

    // Context-aware background property adjustments
    const isDarkBackground = backgroundColors.canvasBgColor ? (() => {
      const rgb = hexToRgb(backgroundColors.canvasBgColor);
      if (!rgb) return false;
      // Calculate perceived brightness using luminance formula
      const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
      return luminance < 0.4; // Consider dark if luminance < 40%
    })() : false;

    // Adjust properties based on contrast mode and background darkness
    if (isUnifiedTheme) {
      // UNIFIED THEME: Subtle effects since everything is the same color
      setBackgroundBrightness(0.8 + Math.random() * 0.4); // Moderate brightness
      setBackgroundTransparency(0.9 + Math.random() * 0.1); // High transparency for subtle effects
      setBackgroundSaturation(0.3 + Math.random() * 0.4); // Low saturation to maintain unity
    } else if (isHardContrast) {
      // HARD CONTRAST: Maximize brightness and saturation for vibrant effects
      setBackgroundBrightness(1.2 + Math.random() * 0.8); // Higher brightness for vivid effects
      setBackgroundTransparency(0.8 + Math.random() * 0.2); // More opaque for solid colors
      setBackgroundSaturation(1.0 + Math.random() * 0.5); // Maximum saturation
    } else if (isHighContrastHarmony) {
      setBackgroundBrightness(isDarkBackground ? 1.5 + Math.random() * 0.5 : 0.8 + Math.random() * 0.4);
      setBackgroundTransparency(isDarkBackground ? 0.7 + Math.random() * 0.2 : 0.4 + Math.random() * 0.3);
      setBackgroundSaturation(1.0 + Math.random() * 1.0); // Higher saturation for contrast
    } else {
      setBackgroundBrightness(isDarkBackground ? 1.2 + Math.random() * 0.6 : 0.7 + Math.random() * 0.5);
      setBackgroundTransparency(isDarkBackground ? 0.6 + Math.random() * 0.3 : 0.3 + Math.random() * 0.4);
      setBackgroundSaturation(0.8 + Math.random() * 0.8); // Moderate saturation for harmony
    }

    // Theme-specific scale and speed adjustments
    const selectedTheme = themes[themeIndex];
    switch (selectedTheme) {
      case 'particles':
      case 'rain':
        setBackgroundScale(0.8 + Math.random() * 1.2); // Larger scale for particle effects
        setBackgroundAnimationSpeed(0.4 + Math.random() * 1.1);
        break;
      case 'liquid':
      case 'waveform':
        setBackgroundScale(0.6 + Math.random() * 1.4); // Medium scale for fluid effects
        setBackgroundAnimationSpeed(0.2 + Math.random() * 1.3);
        break;
      case 'geometric':
      case 'orbit':
        setBackgroundScale(0.9 + Math.random() * 1.1); // Consistent scale for geometric patterns
        setBackgroundAnimationSpeed(0.1 + Math.random() * 1.4);
        break;
      default:
        setBackgroundScale(0.7 + Math.random() * 1.0);
        setBackgroundAnimationSpeed(0.3 + Math.random() * 1.2);
    }

    // Choose final nav color - allow monochrome options that can match backgrounds
    let finalNavColor: string;
    const useMonochromeNav = isUnifiedTheme ? Math.random() < 0.8 : // 80% chance for monochrome in unified theme
      isHardContrast ? Math.random() < 0.15 : Math.random() < 0.3; // 15% vs 30%

    if (useMonochromeNav) {
      // Choose between white, black, or grey nav colors
      const monochromeOptions = [
        '#ffffff', // White
        '#000000', // Black
        '#1a1a1a', // Dark grey
        '#2a2a2a', // Medium dark grey
        '#3a3a3a', // Medium grey
        '#4a4a4a', // Light grey
        '#f5f5f5', // Light grey (off-white)
        '#e5e5e5', // Medium light grey
        '#d5d5d5', // Light grey
        '#c5c5c5'  // Very light grey
      ];
      finalNavColor = monochromeOptions[Math.floor(Math.random() * monochromeOptions.length)];

      // For monochrome nav, update UI color and potentially background to match
      if (finalNavColor === '#ffffff' || finalNavColor === '#f5f5f5' || finalNavColor === '#e5e5e5' || finalNavColor === '#d5d5d5' || finalNavColor === '#c5c5c5') {
        // Light nav - make UI color darker for contrast
        uiColor = hslToHex(0, 0, 20 + Math.random() * 20); // Dark grey UI
      } else if (finalNavColor === '#000000' || finalNavColor === '#1a1a1a') {
        // Very dark nav - make UI color lighter for contrast
        uiColor = hslToHex(0, 0, 40 + Math.random() * 20); // Medium grey UI
      } else {
        // Medium grey nav - keep UI color as is or slightly adjust
        uiColor = hslToHex(0, 0, 35 + Math.random() * 15); // Medium grey UI
      }

      // For hard contrast mode, if using monochrome nav, update background to match
      if (isHardContrast && Math.random() < 0.7) { // 70% chance to make background match monochrome nav
        backgroundColors.canvasBgColor = finalNavColor;
      }
    } else {
      // Use the generated vibrant color
      finalNavColor = navColor;
    }

    // Set both background colors (effects and canvas background)
    setBackgroundColor(backgroundColors.effectsColor);
    setBackgroundSecondaryColor(backgroundColors.canvasBgColor);
    setTempColor(backgroundColors.effectsColor);
    setTempSecondaryColor(backgroundColors.canvasBgColor);

    // Choose the best text color for maximum readability across all theme colors
    // Use the canvas background color for text contrast calculation (more visible)
    const bestTextColor = chooseBestTextColor(backgroundColors.canvasBgColor, uiColor, finalNavColor);

    // Lock nav colors together and use final nav color with optimal text contrast
    setNavColorsLocked(true);
    setTopNavColor(finalNavColor);
    setSidebarColor(finalNavColor); // Same color as top nav when locked
    setUIColor(uiColor);
    setTextColor(bestTextColor); // Automatically chosen for best contrast
    setTempTopNavColor(finalNavColor);
    setTempSidebarColor(finalNavColor);
    setTempUiColor(uiColor);
    setTempTextColor(bestTextColor);

    // Set container colors to match the nav color when locked
    setContainerColorsLocked(true);
    setContainerColor900(finalNavColor);
    setContainerColor800(uiColor);
    setContainerColor700(hslToHex(palette.uiHue, 40 + Math.random() * 20, 60 + Math.random() * 20));
    setContainerColor600(hslToHex(palette.uiHue, 30 + Math.random() * 20, 80 + Math.random() * 20));
    setTempContainerColor900(finalNavColor);
    setTempContainerColor800(uiColor);
    setTempContainerColor700(hslToHex(palette.uiHue, 40 + Math.random() * 20, 60 + Math.random() * 20));
    setTempContainerColor600(hslToHex(palette.uiHue, 30 + Math.random() * 20, 80 + Math.random() * 20));
  };

  const originalColorsRef = useRef<{ topNav: string, sidebar: string, ui: string, text: string } | null>(null);

  const handleThemeColorChange = (newColor: string) => {
    switch (activeColorPicker) {
      case 'background':
        setTempColor(newColor);
        break;
      case 'secondary':
        setTempSecondaryColor(newColor);
        break;
      case 'topNav':
        setTempTopNavColor(newColor);
        if (navColorsLocked) {
          setTempSidebarColor(newColor);
        }
        break;
      case 'sidebar':
        setTempSidebarColor(newColor);
        if (navColorsLocked) {
          setTempTopNavColor(newColor);
        }
        break;
      case 'ui':
        setTempUiColor(newColor);
        break;
      case 'text':
        setTempTextColor(newColor);
        break;
    }
  };

  const handleContainerColorChange = (newColor: string) => {
    switch (activeContainerPicker) {
      case '900':
        setTempContainerColor900(newColor);
        if (containerColorsLocked) {
          // When locked, update other container colors relative to this one
          const baseRgb = hexToRgb(newColor);
          if (baseRgb) {
            setTempContainerColor800(rgbToHex(Math.max(0, baseRgb.r + 5), Math.max(0, baseRgb.g + 5), Math.max(0, baseRgb.b + 5)));
            setTempContainerColor700(rgbToHex(Math.max(0, baseRgb.r + 10), Math.max(0, baseRgb.g + 10), Math.max(0, baseRgb.b + 10)));
            setTempContainerColor600(rgbToHex(Math.min(255, baseRgb.r + 135), Math.min(255, baseRgb.g + 135), Math.min(255, baseRgb.b + 135)));
          }
        }
        break;
      case '800':
        setTempContainerColor800(newColor);
        if (containerColorsLocked) {
          const baseRgb = hexToRgb(newColor);
          if (baseRgb) {
            setTempContainerColor900(rgbToHex(Math.max(0, baseRgb.r - 5), Math.max(0, baseRgb.g - 5), Math.max(0, baseRgb.b - 5)));
            setTempContainerColor700(rgbToHex(Math.max(0, baseRgb.r + 5), Math.max(0, baseRgb.g + 5), Math.max(0, baseRgb.b + 5)));
            setTempContainerColor600(rgbToHex(Math.min(255, baseRgb.r + 130), Math.min(255, baseRgb.g + 130), Math.min(255, baseRgb.b + 130)));
          }
        }
        break;
      case '700':
        setTempContainerColor700(newColor);
        if (containerColorsLocked) {
          const baseRgb = hexToRgb(newColor);
          if (baseRgb) {
            setTempContainerColor900(rgbToHex(Math.max(0, baseRgb.r - 10), Math.max(0, baseRgb.g - 10), Math.max(0, baseRgb.b - 10)));
            setTempContainerColor800(rgbToHex(Math.max(0, baseRgb.r - 5), Math.max(0, baseRgb.g - 5), Math.max(0, baseRgb.b - 5)));
            setTempContainerColor600(rgbToHex(Math.min(255, baseRgb.r + 125), Math.min(255, baseRgb.g + 125), Math.min(255, baseRgb.b + 125)));
          }
        }
        break;
      case '600':
        setTempContainerColor600(newColor);
        if (containerColorsLocked) {
          const baseRgb = hexToRgb(newColor);
          if (baseRgb) {
            setTempContainerColor900(rgbToHex(Math.max(0, baseRgb.r - 135), Math.max(0, baseRgb.g - 135), Math.max(0, baseRgb.b - 135)));
            setTempContainerColor800(rgbToHex(Math.max(0, baseRgb.r - 130), Math.max(0, baseRgb.g - 130), Math.max(0, baseRgb.b - 130)));
            setTempContainerColor700(rgbToHex(Math.max(0, baseRgb.r - 125), Math.max(0, baseRgb.g - 125), Math.max(0, baseRgb.b - 125)));
          }
        }
        break;
    }
  };

  const handleGlobalMouseUp = useCallback(() => {
    if (isDraggingColor) {
      setIsDraggingColor(false);

      // Commit the color changes based on active picker
      switch (activeColorPicker) {
        case 'background':
          if (tempColor !== backgroundColor) {
            setBackgroundColor(tempColor);
          }
          break;
        case 'secondary':
          if (tempSecondaryColor !== backgroundSecondaryColor) {
            setBackgroundSecondaryColor(tempSecondaryColor);
          }
          break;
        case 'topNav':
          if (tempTopNavColor !== topNavColor) {
            setTopNavColor(tempTopNavColor);
            if (navColorsLocked) {
              setSidebarColor(tempTopNavColor);
            }
          }
          break;
        case 'sidebar':
          if (tempSidebarColor !== sidebarColor) {
            setSidebarColor(tempSidebarColor);
            if (navColorsLocked) {
              setTopNavColor(tempSidebarColor);
            }
          }
          break;
        case 'ui':
          if (tempUiColor !== uiColor) {
            setUIColor(tempUiColor);
          }
          break;
        case 'text':
          if (tempTextColor !== textColor) {
            setTextColor(tempTextColor);
          }
          // Handle container color commits
          if (activeContainerPicker === '900' && tempContainerColor900 !== containerColor900) {
            setContainerColor900(tempContainerColor900);
            if (containerColorsLocked) {
              setContainerColor800(tempContainerColor800);
              setContainerColor700(tempContainerColor700);
              setContainerColor600(tempContainerColor600);
            }
          } else if (activeContainerPicker === '800' && tempContainerColor800 !== containerColor800) {
            setContainerColor800(tempContainerColor800);
            if (containerColorsLocked) {
              setContainerColor900(tempContainerColor900);
              setContainerColor700(tempContainerColor700);
              setContainerColor600(tempContainerColor600);
            }
          } else if (activeContainerPicker === '700' && tempContainerColor700 !== containerColor700) {
            setContainerColor700(tempContainerColor700);
            if (containerColorsLocked) {
              setContainerColor900(tempContainerColor900);
              setContainerColor800(tempContainerColor800);
              setContainerColor600(tempContainerColor600);
            }
          } else if (activeContainerPicker === '600' && tempContainerColor600 !== containerColor600) {
            setContainerColor600(tempContainerColor600);
            if (containerColorsLocked) {
              setContainerColor900(tempContainerColor900);
              setContainerColor800(tempContainerColor800);
              setContainerColor700(tempContainerColor700);
            }
          }
          break;
      }
    }
  }, [isDraggingColor, activeColorPicker, activeContainerPicker, tempColor, backgroundColor, tempSecondaryColor, backgroundSecondaryColor, tempTopNavColor, topNavColor, tempSidebarColor, sidebarColor, tempUiColor, uiColor, tempTextColor, textColor, navColorsLocked, tempContainerColor900, containerColor900, tempContainerColor800, containerColor800, tempContainerColor700, containerColor700, tempContainerColor600, containerColor600, containerColorsLocked, setBackgroundColor, setBackgroundSecondaryColor, setTopNavColor, setSidebarColor, setUIColor, setTextColor, setContainerColor900, setContainerColor800, setContainerColor700, setContainerColor600]);

  const handleColorPickerMouseDown = () => {
    setIsDraggingColor(true);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPickerOpen) {
      // Colors are already updated immediately, just close the picker
      setIsPickerOpen(false);
      setIsDraggingColor(false); // Reset dragging state
    } else {
      // If picker is closed, open it with current color
      setTempColor(backgroundColor);
      setTempSecondaryColor(backgroundSecondaryColor);
      setTempTopNavColor(topNavColor);
      setTempSidebarColor(sidebarColor);
      setTempUiColor(uiColor);
      setTempTextColor(textColor);
      setTempContainerColor900(containerColor900);
      setTempContainerColor800(containerColor800);
      setTempContainerColor700(containerColor700);
      setTempContainerColor600(containerColor600);
      // Store original colors in case we need to revert
      originalColorsRef.current = {
        topNav: topNavColor,
        sidebar: sidebarColor,
        ui: uiColor,
        text: textColor
      };
      setIsDraggingColor(false); // Reset dragging state
      setIsPickerOpen(true);
    }
  };

  useEffect(() => {
    if (!isPickerOpen) {
      setTempColor(backgroundColor);
      setTempSecondaryColor(backgroundSecondaryColor);
      setTempTopNavColor(topNavColor);
      setTempSidebarColor(sidebarColor);
      setTempUiColor(uiColor);
      setTempTextColor(textColor);
      setTempContainerColor900(containerColor900);
      setTempContainerColor800(containerColor800);
      setTempContainerColor700(containerColor700);
      setTempContainerColor600(containerColor600);
    }
  }, [backgroundColor, backgroundSecondaryColor, topNavColor, sidebarColor, uiColor, textColor, containerColor900, containerColor800, containerColor700, containerColor600, isPickerOpen]);

  useEffect(() => {
    if (lastRandomMode === 'hard-contrast' || lastRandomMode === 'hard-contrast-nav-match' || lastRandomMode === 'unified-theme') {
      const timer = setTimeout(() => {
        setLastRandomMode('normal');
      }, 4000); // Reset after 4 seconds for special modes
      return () => clearTimeout(timer);
    }
  }, [lastRandomMode]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const isClickOnIcon = iconRef.current && iconRef.current.contains(e.target as Node);
      const isClickOnPicker = pickerRef.current && pickerRef.current.contains(e.target as Node);

      if (!isClickOnIcon && !isClickOnPicker) {
        // Colors are already updated immediately, just close the picker
        setIsPickerOpen(false);
      }
    };

    const handleMouseUp = () => {
      handleGlobalMouseUp();
    };

    if (isPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPickerOpen, handleGlobalMouseUp]);

  return (
    <div className="relative">
      <div
        ref={iconRef}
        onClick={handleClick}
        className={cn(
          "p-2 rounded-full bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-700/60 cursor-pointer flex items-center justify-center relative overflow-hidden color-wheel-button",
          isPickerOpen && "ring-2 ring-[rgb(var(--accent-1))]/30"
        )}
        style={{ backgroundColor: backgroundColor + '25' }}
      >
        <Palette className="h-5 w-5 text-zinc-400 hover:text-zinc-200 duration-300" />
      </div>
      {isPickerOpen && (
        <div
          ref={pickerRef}
          className="absolute top-full left-0 right-0 mx-auto mt-2 w-80 rounded-lg border border-zinc-700 backdrop-blur-sm shadow-lg z-[9999]"
          style={{ backgroundColor: topNavColor + 'f2' }}
        >
          {/* Close Button */}
          <div className="flex justify-end p-2 border-b border-zinc-700 w-full">
            {/* Tab Navigation */}
            <TabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onColorPickerChange={setActiveColorPicker}
            />
            {/* Vertical Separator */}
            <div className="h-4 w-px bg-zinc-600 mx-2"></div>
            <button
              onClick={() => {
                setIsPickerOpen(false);
              }}
              className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200"
              title="Close and apply changes"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Background Tab Content */}
          {activeTab === 'background' && (
            <BackgroundTab
              backgroundStyle={backgroundStyle}
              onBackgroundStyleChange={setBackgroundStyle}
              activeColorPicker={activeColorPicker}
              tempColor={tempColor}
              tempSecondaryColor={tempSecondaryColor}
              onColorPickerChange={(picker) => setActiveColorPicker(picker)}
              onColorChange={handleThemeColorChange}
              onMouseDown={handleColorPickerMouseDown}
              onMouseUp={() => {
                // Commit changes on mouse release without closing menu
                if (activeColorPicker === 'background' && tempColor !== backgroundColor) {
                  setBackgroundColor(tempColor);
                } else if (activeColorPicker === 'secondary' && tempSecondaryColor !== backgroundSecondaryColor) {
                  setBackgroundSecondaryColor(tempSecondaryColor);
                }
              }}
              onTouchEnd={() => {
                // Commit changes on touch end without closing menu
                if (activeColorPicker === 'background' && tempColor !== backgroundColor) {
                  setBackgroundColor(tempColor);
                } else if (activeColorPicker === 'secondary' && tempSecondaryColor !== backgroundSecondaryColor) {
                  setBackgroundSecondaryColor(tempSecondaryColor);
                }
              }}
              isAdvancedOpen={isAdvancedOpen}
              onAdvancedToggle={() => setIsAdvancedOpen(!isAdvancedOpen)}
              backgroundBrightness={backgroundBrightness}
              onBrightnessChange={setBackgroundBrightness}
              backgroundTransparency={backgroundTransparency}
              onTransparencyChange={setBackgroundTransparency}
              backgroundSaturation={backgroundSaturation}
              onSaturationChange={setBackgroundSaturation}
              backgroundScale={backgroundScale}
              onScaleChange={setBackgroundScale}
              backgroundAnimationSpeed={backgroundAnimationSpeed}
              onAnimationSpeedChange={setBackgroundAnimationSpeed}
            />
          )}

          {/* Theme Tab Content */}
          {activeTab === 'theme' && (
            <ThemeTab
              fontFamily={fontFamily}
              onFontFamilyChange={setFontFamily}
              fontSize={fontSize}
              onFontSizeChange={setFontSize}
              isNavColorsOpen={isNavColorsOpen}
              onNavColorsToggle={() => setIsNavColorsOpen(!isNavColorsOpen)}
              navColorsLocked={navColorsLocked}
              onNavColorsLockToggle={() => setNavColorsLocked(!navColorsLocked)}
              tempTopNavColor={tempTopNavColor}
              tempSidebarColor={tempSidebarColor}
              onTopNavColorClick={() => setActiveColorPicker('topNav')}
              onSidebarColorClick={() => setActiveColorPicker('sidebar')}
              tempUiColor={tempUiColor}
              tempTextColor={tempTextColor}
              onUiColorClick={() => setActiveColorPicker('ui')}
              onTextColorClick={() => setActiveColorPicker('text')}
              isContainerColorsOpen={isContainerColorsOpen}
              onContainerColorsToggle={() => setIsContainerColorsOpen(!isContainerColorsOpen)}
              containerColorsLocked={containerColorsLocked}
              onContainerColorsLockToggle={() => setContainerColorsLocked(!containerColorsLocked)}
              tempContainerColor900={tempContainerColor900}
              tempContainerColor800={tempContainerColor800}
              tempContainerColor700={tempContainerColor700}
              tempContainerColor600={tempContainerColor600}
              onContainerColor900Click={() => {
                setActiveColorPicker('text');
                setActiveContainerPicker('900');
              }}
              onContainerColor800Click={() => {
                setActiveColorPicker('text');
                setActiveContainerPicker('800');
              }}
              onContainerColor700Click={() => {
                setActiveColorPicker('text');
                setActiveContainerPicker('700');
              }}
              onContainerColor600Click={() => {
                setActiveColorPicker('text');
                setActiveContainerPicker('600');
              }}
              containerTransparency={containerTransparency}
              onContainerTransparencyChange={setContainerTransparency}
              activeColorPicker={activeColorPicker}
              onColorChange={activeColorPicker === 'text' && activeContainerPicker !== '800' ? handleContainerColorChange : handleThemeColorChange}
              onMouseDown={handleColorPickerMouseDown}
              onMouseUp={() => {
                // Commit changes on mouse release without closing menu
                if (activeColorPicker === 'topNav' && tempTopNavColor !== topNavColor) {
                  setTopNavColor(tempTopNavColor);
                  if (navColorsLocked) {
                    setSidebarColor(tempTopNavColor);
                  }
                } else if (activeColorPicker === 'sidebar' && tempSidebarColor !== sidebarColor) {
                  setSidebarColor(tempSidebarColor);
                  if (navColorsLocked) {
                    setTopNavColor(tempSidebarColor);
                  }
                } else if (activeColorPicker === 'ui' && tempUiColor !== uiColor) {
                  setUIColor(tempUiColor);
                } else if (activeColorPicker === 'text' && tempTextColor !== textColor) {
                  setTextColor(tempTextColor);
                }
                // Handle container color commits when editing container colors
                if (activeColorPicker === 'text') {
                  if (activeContainerPicker === '900' && tempContainerColor900 !== containerColor900) {
                    setContainerColor900(tempContainerColor900);
                    if (containerColorsLocked) {
                      setContainerColor800(tempContainerColor800);
                      setContainerColor700(tempContainerColor700);
                      setContainerColor600(tempContainerColor600);
                    }
                  } else if (activeContainerPicker === '800' && tempContainerColor800 !== containerColor800) {
                    setContainerColor800(tempContainerColor800);
                    if (containerColorsLocked) {
                      setContainerColor900(tempContainerColor900);
                      setContainerColor700(tempContainerColor700);
                      setContainerColor600(tempContainerColor600);
                    }
                  } else if (activeContainerPicker === '700' && tempContainerColor700 !== containerColor700) {
                    setContainerColor700(tempContainerColor700);
                    if (containerColorsLocked) {
                      setContainerColor900(tempContainerColor900);
                      setContainerColor800(tempContainerColor800);
                      setContainerColor600(tempContainerColor600);
                    }
                  } else if (activeContainerPicker === '600' && tempContainerColor600 !== containerColor600) {
                    setContainerColor600(tempContainerColor600);
                    if (containerColorsLocked) {
                      setContainerColor900(tempContainerColor900);
                      setContainerColor800(tempContainerColor800);
                      setContainerColor700(tempContainerColor700);
                    }
                  }
                }
              }}
              onTouchEnd={() => {
                // Commit changes on touch end without closing menu
                if (activeColorPicker === 'topNav' && tempTopNavColor !== topNavColor) {
                  setTopNavColor(tempTopNavColor);
                  if (navColorsLocked) {
                    setSidebarColor(tempTopNavColor);
                  }
                } else if (activeColorPicker === 'sidebar' && tempSidebarColor !== sidebarColor) {
                  setSidebarColor(tempSidebarColor);
                  if (navColorsLocked) {
                    setTopNavColor(tempSidebarColor);
                  }
                } else if (activeColorPicker === 'ui' && tempUiColor !== uiColor) {
                  setUIColor(tempUiColor);
                } else if (activeColorPicker === 'text' && tempTextColor !== textColor) {
                  setTextColor(tempTextColor);
                }
                // Handle container color commits when editing container colors
                if (activeColorPicker === 'text') {
                  if (activeContainerPicker === '900' && tempContainerColor900 !== containerColor900) {
                    setContainerColor900(tempContainerColor900);
                    if (containerColorsLocked) {
                      setContainerColor800(tempContainerColor800);
                      setContainerColor700(tempContainerColor700);
                      setContainerColor600(tempContainerColor600);
                    }
                  } else if (activeContainerPicker === '800' && tempContainerColor800 !== containerColor800) {
                    setContainerColor800(tempContainerColor800);
                    if (containerColorsLocked) {
                      setContainerColor900(tempContainerColor900);
                      setContainerColor700(tempContainerColor700);
                      setContainerColor600(tempContainerColor600);
                    }
                  } else if (activeContainerPicker === '700' && tempContainerColor700 !== containerColor700) {
                    setContainerColor700(tempContainerColor700);
                    if (containerColorsLocked) {
                      setContainerColor900(tempContainerColor900);
                      setContainerColor800(tempContainerColor800);
                      setContainerColor600(tempContainerColor600);
                    }
                  } else if (activeContainerPicker === '600' && tempContainerColor600 !== containerColor600) {
                    setContainerColor600(tempContainerColor600);
                    if (containerColorsLocked) {
                      setContainerColor900(tempContainerColor900);
                      setContainerColor800(tempContainerColor800);
                      setContainerColor700(tempContainerColor700);
                    }
                  }
                }
              }}
              activeContainerPicker={activeContainerPicker}
            />
          )}

          {/* Action Buttons */}
          <ActionButtons
            onReset={handleResetDefaults}
            onRandomize={handleRandomize}
            onSave={async (e) => {
              e.stopPropagation();
              try {
                await saveTheme();
                setIsPickerOpen(false);
                const event = new CustomEvent('toast:push', {
                  detail: {
                    message: 'Theme saved successfully!',
                    type: 'success',
                    duration: 3000
                  }
                });
                window.dispatchEvent(event);
              } catch (e) {
                console.error('Save theme error:', e);
                const event = new CustomEvent('toast:push', {
                  detail: {
                    message: 'Failed to save theme. Please try again.',
                    type: 'error',
                    duration: 5000
                  }
                });
                window.dispatchEvent(event);
              }
            }}
          />

        </div>
      )}
    </div>
  );
};

export default ColorWheelIcon;